import React, { useEffect, useMemo } from 'react';
import { useJsonShare, getRoomKeyFromState } from './hooks/useJsonShare';
import { JsonEditor } from './components/JsonEditor';
import { RoomList } from './components/RoomList';
import { ShareControls } from './components/ShareControls';

const App: React.FC = () => {
  // Get room key from URL if present
  const urlParams = new URLSearchParams(window.location.search);
  const urlRoomKey = urlParams.get('room');

  // Initialize hook with empty JSON
  const {
    json,
    setJson,
    roomName,
    isConnected,
    roomState,
    rooms,
    createRoom,
    startSharing,
    stopSharing,
    joinRoom,
    selectRoom,
    deleteRoom,
  } = useJsonShare({ initialJson: {} });

  // Derived state
  const activeRoomKey = useMemo(() => getRoomKeyFromState(roomState), [roomState]);

  // If URL has a room key, try to join that room
  useEffect(() => {
    if (urlRoomKey && !activeRoomKey) {
      // Try to join the room (will load from IndexedDB or connect to Yjs)
      joinRoom(urlRoomKey);
    }
  }, [urlRoomKey, activeRoomKey, joinRoom]);

  // Update URL when room changes
  // Clear URL when in unselected or sharedStopped state
  useEffect(() => {
    const shouldHaveRoomInUrl = activeRoomKey && roomState.type !== 'sharedStopped';
    
    const newUrl = new URL(window.location.href);
    if (shouldHaveRoomInUrl) {
      newUrl.searchParams.set('room', activeRoomKey);
    } else {
      newUrl.searchParams.delete('room');
    }
    window.history.replaceState({}, '', newUrl);
  }, [activeRoomKey, roomState.type]);

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '24px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
    }}>
      <header style={{ marginBottom: '32px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>JSON Delen</h1>
        <p style={{ margin: '8px 0 0 0', color: '#666' }}>
          Deel en bewerk JSON in real-time tussen meerdere browsers
        </p>
      </header>

      <main>
        <RoomList
          rooms={rooms}
          currentRoomKey={activeRoomKey}
          roomState={roomState}
          onSelectRoom={selectRoom}
          onDeleteRoom={deleteRoom}
          onCreateRoom={createRoom}
        />

        {activeRoomKey && (
          <ShareControls
            roomKey={activeRoomKey}
            roomName={roomName}
            roomState={roomState}
            isConnected={isConnected}
            onStartSharing={startSharing}
            onStopSharing={stopSharing}
            onDeleteRoom={deleteRoom}
          />
        )}

        <div style={{ marginTop: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', marginBottom: '12px' }}>
            JSON Bewerker
          </h2>
          <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
            {/* Alleen-lezen wanneer delen is gestopt door de initiator */}
            <JsonEditor json={json} onChange={setJson} readOnly={roomState.type === 'sharedStopped'} />
          </div>
        </div>

        {roomState.type === 'sharedStopped' && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            background: '#f8d7da',
            borderRadius: '8px',
            border: '1px solid #f5c6cb',
          }}>
            <p style={{ margin: 0, color: '#721c24' }}>
              <strong>Fout:</strong> Deze JSON wordt niet meer gedeeld. De eigenaar heeft het delen beëindigd.
            </p>
          </div>
        )}

        {!activeRoomKey && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            background: '#e7f3ff',
            borderRadius: '8px',
            border: '1px solid #b3d9ff',
          }}>
            <p style={{ margin: 0, color: '#004085' }}>
              <strong>Tip:</strong> Maak of selecteer een kamer om JSON te bewerken en te delen.
            </p>
          </div>
        )}
      </main>

      <footer style={{ marginTop: '48px', paddingTop: '16px', borderTop: '1px solid #eee', color: '#666', fontSize: '0.85em' }}>
        <p>
          Aangedreven door Yjs voor real-time synchronisatie. Kamers worden automatisch opgeruimd wanneer de initiator stopt met delen.
        </p>
      </footer>
    </div>
  );
};

export default App;
